/**
 * TechPackDocument — the five-page tech-pack body, shared by the pack
 * PREVIEW screen (design's revision, pre-generation) and the pack VIEWER
 * (`/tech-packs/$packId`, a generated pack's pinned revision). Same page
 * grammar as the server PDF; the flat figures come from the domain's
 * flat-svg strings so screen and document never drift.
 */
import { flatWithCalloutsSvg } from "@flint/domain/garment/designs/flat-svg";
import { formatDate } from "../../../../lib/format.js";
import { garmentTypeLabel } from "../../lib/garment-type.js";
import { FLAT_THEME_VARS } from "./garment-flat.tsx";
import { elementLabel, mmToCmText } from "./element-meta.js";

export interface TechPackElement {
  id: string;
  elementType: string;
  view: string;
  x: number;
  y: number;
  widthMm: number | null;
  heightMm: number | null;
  offsetNote: string | null;
  note: string | null;
  colourway: string | null;
}

export interface TechPackDocumentModel {
  styleRef: string;
  buyerName: string;
  garmentType: string;
  name: string;
  poNumber: string | null;
  exFactoryDate: string | null;
  lines: Array<{ size: string; qty: number }>;
  elements: TechPackElement[];
}

export function TechPackDocument({
  model,
  emptyHint,
  provenance,
}: {
  model: TechPackDocumentModel;
  /** Shown under the flats when there are no markings at all. */
  emptyHint?: string;
  /** The last page's footer line (generation provenance / preview note). */
  provenance: React.ReactNode;
}) {
  const front = model.elements.filter((e) => e.view === "front");
  const back = model.elements.filter((e) => e.view === "back");
  const packingElements = model.elements.filter((e) => e.elementType === "packing");
  const colourways = [
    ...new Set(model.elements.map((e) => e.colourway).filter((c): c is string => !!c)),
  ];
  const totalQty = model.lines.reduce((sum, line) => sum + line.qty, 0);

  return (
    <main className="mx-auto flex w-full max-w-[900px] flex-col gap-6 px-6 py-6 print:max-w-none print:gap-0 print:p-0">
      {/* ── Page 1 · Flat with callouts ── */}
      <PackPage
        title="Garment flat & markings"
        styleRef={model.styleRef}
        buyer={model.buyerName}
        footer={`${garmentTypeLabel(model.garmentType)} · ${model.name}`}
      >
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FlatWithCallouts label="Front" garmentType={model.garmentType} view="front" elements={front} />
          <FlatWithCallouts label="Back" garmentType={model.garmentType} view="back" elements={back} />
        </div>
        {model.elements.length === 0 && emptyHint ? (
          <p className="mt-4 rounded-md bg-raised px-3 py-2 text-center text-sm text-muted">
            {emptyHint}
          </p>
        ) : null}
      </PackPage>

      {/* ── Page 2 · Marking details ── */}
      <PackPage title="Marking details" styleRef={model.styleRef} buyer={model.buyerName}>
        {model.elements.length === 0 ? (
          <p className="text-sm text-muted">No markings.</p>
        ) : (
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-y border-line text-xs uppercase tracking-wide text-muted">
                <th className="py-2 pr-2 text-left font-medium">#</th>
                <th className="py-2 pr-2 text-left font-medium">Marking</th>
                <th className="py-2 pr-2 text-left font-medium">View</th>
                <th className="py-2 pr-2 text-right font-medium">W × H (cm)</th>
                <th className="py-2 pr-2 text-left font-medium">Placement</th>
                <th className="py-2 text-left font-medium">Note</th>
              </tr>
            </thead>
            <tbody>
              {model.elements.map((element, index) => (
                <tr key={element.id} className="border-b border-line align-top">
                  <td className="py-2 pr-2 font-mono text-xs text-muted">{index + 1}</td>
                  <td className="py-2 pr-2 font-medium text-ink">{elementLabel(element.elementType)}</td>
                  <td className="py-2 pr-2 capitalize text-muted">{element.view}</td>
                  <td className="py-2 pr-2 text-right font-mono text-ink-2">
                    {element.widthMm || element.heightMm
                      ? `${mmToCmText(element.widthMm) || "—"} × ${mmToCmText(element.heightMm) || "—"}`
                      : "—"}
                  </td>
                  <td className="py-2 pr-2 text-ink-2">{element.offsetNote ?? "—"}</td>
                  <td className="py-2 text-muted">{element.note ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PackPage>

      {/* ── Page 3 · Colourways ── */}
      <PackPage title="Colourways" styleRef={model.styleRef} buyer={model.buyerName}>
        {colourways.length === 0 ? (
          <p className="text-sm text-muted">
            Single colourway (as ordered) — colour splits arrive with the Style File's ratio grid.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-3">
            {colourways.map((colourway) => (
              <li key={colourway} className="rounded-md border border-line px-4 py-3 text-sm font-medium text-ink">
                {colourway}
              </li>
            ))}
          </ul>
        )}
      </PackPage>

      {/* ── Page 4 · Workorder (auto-filled from the Style File) ── */}
      <PackPage title="Workorder" styleRef={model.styleRef} buyer={model.buyerName}>
        <dl className="mb-4 grid grid-cols-2 gap-x-8 gap-y-1.5 text-sm sm:grid-cols-3">
          <div className="flex justify-between gap-2 sm:block">
            <dt className="text-xs uppercase tracking-wide text-muted">Style ref</dt>
            <dd className="font-mono font-semibold text-ink">{model.styleRef}</dd>
          </div>
          <div className="flex justify-between gap-2 sm:block">
            <dt className="text-xs uppercase tracking-wide text-muted">PO number</dt>
            <dd className="font-mono text-ink-2">{model.poNumber ?? "—"}</dd>
          </div>
          <div className="flex justify-between gap-2 sm:block">
            <dt className="text-xs uppercase tracking-wide text-muted">Ex-factory</dt>
            <dd className="font-mono text-ink-2">
              {model.exFactoryDate ? formatDate(model.exFactoryDate) : "—"}
            </dd>
          </div>
          <div className="flex justify-between gap-2 sm:block">
            <dt className="text-xs uppercase tracking-wide text-muted">Garment</dt>
            <dd className="text-ink-2">{garmentTypeLabel(model.garmentType)}</dd>
          </div>
          <div className="flex justify-between gap-2 sm:block">
            <dt className="text-xs uppercase tracking-wide text-muted">Buyer</dt>
            <dd className="font-mono text-ink-2">{model.buyerName}</dd>
          </div>
        </dl>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-y border-line text-xs uppercase tracking-wide text-muted">
              <th className="w-28 py-2 pr-3 text-left font-medium">Size</th>
              {model.lines.map((line) => (
                <th key={line.size} className="px-3 py-2 text-right font-semibold text-ink">
                  {line.size}
                </th>
              ))}
              <th className="border-l border-line py-2 pl-3 text-right font-semibold text-ink">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-line">
              <td className="py-2.5 pr-3 text-xs uppercase tracking-wide text-muted">Qty (pcs)</td>
              {model.lines.map((line) => (
                <td key={line.size} className="px-3 py-2.5 text-right font-mono text-ink-2">
                  {line.qty.toLocaleString("en-IN")}
                </td>
              ))}
              <td className="border-l border-line py-2.5 pl-3 text-right font-mono font-bold text-ink">
                {totalQty.toLocaleString("en-IN")}
              </td>
            </tr>
          </tbody>
        </table>
      </PackPage>

      {/* ── Page 5 · Packing ── */}
      <PackPage title="Packing & accessories" styleRef={model.styleRef} buyer={model.buyerName} last>
        {packingElements.length === 0 ? (
          <p className="text-sm text-muted">No packing markings recorded — standard packing applies.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {packingElements.map((element) => (
              <li key={element.id} className="rounded-md border border-line px-3 py-2 text-sm">
                <span className="font-medium text-ink">{element.note ?? "Packing item"}</span>
                {element.offsetNote ? <span className="ml-2 text-muted">{element.offsetNote}</span> : null}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-6 border-t border-line pt-3 text-xs text-muted">{provenance}</p>
      </PackPage>
    </main>
  );
}

/** One printable page — bordered card on screen, a page in print. */
function PackPage({
  title,
  styleRef,
  buyer,
  footer,
  last = false,
  children,
}: {
  title: string;
  styleRef: string;
  buyer: string;
  footer?: string;
  last?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`rounded-md border border-line bg-paper p-6 shadow-sm print:rounded-none print:border-0 print:shadow-none ${
        last ? "" : "print:break-after-page"
      }`}
    >
      <div className="mb-4 flex items-baseline justify-between border-b border-line pb-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-ink">{title}</h2>
        <span className="font-mono text-xs text-muted">
          {styleRef} · {buyer}
        </span>
      </div>
      {children}
      {footer ? <p className="mt-4 text-xs text-muted">{footer}</p> : null}
    </section>
  );
}

/**
 * The tech-pack figure: flat + drawn glyphs + non-crossing margin callouts.
 * Drawn by the domain's flat-svg grammar — the same string the server PDF
 * renders, so screen and document never drift.
 */
function FlatWithCallouts({
  label,
  garmentType,
  view,
  elements,
}: {
  label: string;
  garmentType: string;
  view: "front" | "back";
  elements: TechPackElement[];
}) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">{label}</h3>
      <div
        className="mx-auto w-full max-w-[360px] [&>svg]:h-auto [&>svg]:w-full"
        style={FLAT_THEME_VARS}
        // Trusted geometry from our own domain module — no user input reaches
        // the markup unescaped (flat-svg escapes labels/notes).
        dangerouslySetInnerHTML={{ __html: flatWithCalloutsSvg({ garmentType, view, elements }) }}
      />
      {elements.length === 0 ? (
        <p className="mt-1 text-center text-xs text-muted">No markings on this view.</p>
      ) : null}
    </div>
  );
}
